import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import useProfileSurveys from 'services/surveys/useProfileSurveys';
import useSurveyResponses from 'services/surveys/surveyResponses';
import useProfileQuestions from 'services/general/profileQuestions';
import useSampleInfoQuestions from 'services/surveys/useSampleInfoQuestions';
import { importantQuestionsForProfiles } from 'services/surveys/getImportantSurveyQuestionsWithProfile';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { SurveyResponsesType, QuestionAnswerPairType } from 'types/Surveys';
import { ProfileQuestionsGroup } from 'types/ProfileQuestions';
import { getSurveysStatusForKitId } from 'store/kits';

import {
  PageContainer,
  QuestionAnswerList,
  SectionPlaceholder,
  Header,
  Spinfinity,
  Typography,
} from 'components';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import { SurveyCard } from '../SurveyCard/SurveyCard';
import { SurveyChangesCard } from '../SurveysDetail/SurveysDetail';
import { TypeformSurveyCard } from '../TypeformSurveyCard/TypeformSurveyCard';
import { ProfileAnswersCard } from '../ProfileAnswersCard/ProfileAnswersCard';
import { SampleInfoAnswersCard } from '../SampleInfoAnswersCard/SampleInfoAnswersCard';

import styles from './SurveysSummary.module.css';

export const getSubmittedDate = (
  surveyResponses: SurveyResponsesType[],
  formId: string,
) => {
  const surveyResponseOfFormId = surveyResponses.find(
    response => response.form_id === formId,
  );
  return surveyResponseOfFormId?.submitted_at
    ? surveyResponseOfFormId.submitted_at.slice(0, 10) //use only date
    : '';
};

export const getAllQuestionsAnswers = (
  surveyResponses: SurveyResponsesType[],
  onlyFormIds?: string[],
) => {
  let all: QuestionAnswerPairType[] = [];
  surveyResponses.forEach(response => {
    if (!onlyFormIds?.length || onlyFormIds.includes(response.form_id)) {
      all = [...all, ...response.questions];
    }
  });
  return all;
};

export const getAllProfileQuestionsAnswers = (
  profileQuestions: ProfileQuestionsGroup[] | null,
  lookupCodes?: string[],
) => {
  let all: QuestionAnswerPairType[] = [];
  let tempPair: QuestionAnswerPairType;
  if (!profileQuestions?.length) return all;
  profileQuestions.forEach(questionGroup => {
    questionGroup.questions.forEach(question => {
      if (!lookupCodes?.length || lookupCodes.includes(question.lookup_code)) {
        tempPair = {
          question_id: question.id + '',
          title: question.question_text,
          answer: question.answer_text,
        };
        all = [...all, tempPair];
      }
    });
  });
  return all;
};

export const ProfileQuestions = () => {
  const { currentKitId, currentTinyAccountId } = useCurrentKitAndTinyAccount();
  const { data: profileQuestions } = useProfileQuestions(
    currentTinyAccountId,
    currentKitId,
  );
  const profileQandA = getAllProfileQuestionsAnswers(
    profileQuestions,
    importantQuestionsForProfiles,
  );

  return (
    <div>
      <div className={styles.surveyUnderline}>
        <Typography variant='eyebrow'>Profile</Typography>
      </div>
      <QuestionAnswerList list={profileQandA} />
    </div>
  );
};

export const SampleInfoQuestions = () => {
  const { currentKitId, currentTinyAccountId } = useCurrentKitAndTinyAccount();
  const { data: sampleInfoQuestions } = useSampleInfoQuestions(
    currentTinyAccountId,
    currentKitId,
  );
  const profileQandA = getAllProfileQuestionsAnswers(
    sampleInfoQuestions,
    importantQuestionsForProfiles,
  );

  return (
    <div>
      <div className={styles.surveyUnderline}>
        <Typography variant='eyebrow'>Sample info</Typography>
      </div>
      <QuestionAnswerList list={profileQandA} />
    </div>
  );
};

export const SurveysSummary = () => {
  const { currentKitId, currentTinyAccountId } = useCurrentKitAndTinyAccount();
  const { isNewProfileFlow } = useProfileSurveys(
    currentTinyAccountId,
    currentKitId,
  );

  return isNewProfileFlow ? (
    <SurveysSummaryWithProfile />
  ) : (
    <LegacySurveysSummary />
  );
};

const SurveysSummaryWithProfile = () => {
  const { currentKit, currentKitId, currentTinyAccount, currentTinyAccountId } =
    useCurrentKitAndTinyAccount();
  //surveys check
  const { data: surveys } = useProfileSurveys(
    currentTinyAccountId,
    currentKitId,
  );
  const {
    data: surveyResponses,
    importantQuestionsGrouped,
    kitBelongsToAccount,
    isLoading,
  } = useSurveyResponses(currentKitId, true);

  if (!currentKit || !currentTinyAccount) return null;
  if (!kitBelongsToAccount) {
    return (
      <>
        <Breadcrumb className={styles.breadcrumb} />
        <Header title='What you told us' />
        <PageContainer>
          <div className={styles.sectionsWrapper}>
            <main className={styles.mainContent}>
              <SectionPlaceholder
                title={`No access to kit ${currentKitId} under this account`}
              />
            </main>
          </div>
        </PageContainer>
      </>
    );
  }
  if (isLoading) return <Spinfinity />;
  return (
    <>
      <Breadcrumb className={styles.breadcrumb} />
      <Header title='What you told us' />
      <PageContainer>
        <div className={styles.sectionsWrapper}>
          <main className={styles.mainContent}>
            {!!importantQuestionsGrouped?.length && (
              <div className='flex flex-column gap-3'>
                <Typography variant='eyebrow'>Notable Responses</Typography>
                <ProfileQuestions />
                <SampleInfoQuestions />

                {importantQuestionsGrouped.map(group => (
                  <div key={group.displayTitle}>
                    <div className={styles.surveyUnderline}>
                      <Typography variant='eyebrow'>
                        {group.displayTitle}
                      </Typography>
                    </div>

                    <QuestionAnswerList list={group.questions} />
                  </div>
                ))}
              </div>
            )}
          </main>

          <section className={styles.section}>
            {!surveyResponses?.length ? (
              <p>No survey responses found for kit {currentKitId}.</p>
            ) : (
              <div className={styles.cardsContainer}>
                {<ProfileAnswersCard />}
                {<SampleInfoAnswersCard />}
                {surveys
                  ?.filter(survey => !survey.is_complete && !survey.is_partner)
                  .map(surveyType => (
                    <TypeformSurveyCard
                      key={surveyType.typeform_survey_id}
                      typeform_id={surveyType.typeform_survey_id}
                      display_title={surveyType?.name ?? 'Survey'}
                      estimated_time={surveyType.time_estimate}
                      linkTypeform={`/typeform/${currentTinyAccountId}/${currentKitId}/${surveyType.typeform_survey_id}`}
                      needed
                    />
                  ))}
                {surveys
                  ?.filter(survey => survey.is_complete)
                  .map(surveyType => (
                    <TypeformSurveyCard
                      key={surveyType.typeform_survey_id}
                      typeform_id={surveyType.typeform_survey_id}
                      display_title={surveyType?.name ?? 'Survey'}
                      linkDetail={`/results/${currentKitId}/surveys/${surveyType.typeform_survey_id}`}
                      dateSubmitted={getSubmittedDate(
                        surveyResponses,
                        surveyType.typeform_survey_id,
                      )}
                    />
                  ))}
                <SurveyChangesCard />
              </div>
            )}
          </section>
        </div>
      </PageContainer>
    </>
  );
};

const LegacySurveysSummary = () => {
  const { currentKit, currentKitId, currentTinyAccount } =
    useCurrentKitAndTinyAccount();
  //surveys check
  const status = useSelector(getSurveysStatusForKitId(currentKitId)) ?? {
    completed: [],
    pending: [],
    needed: [],
  };

  const {
    data: surveyResponses,
    importantQuestions,
    kitBelongsToAccount,
    isLoading,
  } = useSurveyResponses(currentKitId);
  const onlyImportantQuestionsAnswers = useMemo(
    () =>
      getAllQuestionsAnswers(
        importantQuestions,
        status?.completed?.map(s => s.typeform_id),
      ),
    [importantQuestions, status?.completed],
  );

  if (!currentKit || !currentTinyAccount) return null;
  if (!kitBelongsToAccount) {
    return (
      <>
        {/* SIMPLE Header */}
        <Breadcrumb className={styles.breadcrumb} />
        <Header title='What you told us' />
        <PageContainer>
          <div className={styles.sectionsWrapper}>
            <main className={styles.mainContent}>
              <SectionPlaceholder
                title={`No access to kit ${currentKitId} under this account`}
              />
            </main>
          </div>
        </PageContainer>
      </>
    );
  }
  if (isLoading) return <Spinfinity />;
  return (
    <>
      {/* SIMPLE Header */}
      <Breadcrumb className={styles.breadcrumb} />
      <Header title='What you told us' />
      <PageContainer>
        <div className={styles.sectionsWrapper}>
          {!!onlyImportantQuestionsAnswers?.length && (
            <main className={styles.mainContent}>
              <Typography variant='eyebrow' className={styles.sectionTitle}>
                Notable Responses
              </Typography>
              <QuestionAnswerList list={onlyImportantQuestionsAnswers} />
            </main>
          )}

          <section className={styles.section}>
            <Typography variant='eyebrow' className={styles.sectionTitle}>
              Your surveys
            </Typography>
            {!surveyResponses?.length ? (
              <p>No survey responses found for kit {currentKitId}.</p>
            ) : (
              <div className={styles.cardsContainer}>
                {status?.pending?.map(surveyType => (
                  <SurveyCard
                    key={surveyType.typeform_id}
                    {...surveyType}
                    kit={currentKit}
                    tinyAccount={currentTinyAccount}
                    needed
                  />
                ))}
                {status?.completed?.map(surveyType => (
                  <SurveyCard
                    key={surveyType.typeform_id}
                    {...surveyType}
                    kit={currentKit}
                    tinyAccount={currentTinyAccount}
                    linkDetail={`/results/${currentKitId}/surveys/${surveyType.typeform_id}`}
                    dateSubmitted={getSubmittedDate(
                      surveyResponses,
                      surveyType.typeform_id,
                    )}
                  />
                ))}
                <SurveyChangesCard />
              </div>
            )}
          </section>
        </div>
      </PageContainer>
    </>
  );
};
